/**
 * assets/nb-auto-gifts.js
 *
 * Automatic free-gift reconciliation. No customer opt-in — both gifts are
 * added/removed purely based on how many REAL (non-gift) units are in the
 * cart:
 *
 *   - "Regalo 10ml sorpresa"          (variant 55100408299799): qty 1 as
 *     soon as the cart holds >= 1 real unit. Removed if the cart has none.
 *   - "Dispensador atomizador de viaje" (variant 55100408365335): qty 1 as
 *     soon as the cart holds >= 1 real unit. Removed if the cart has none.
 *
 * 2026-09: threshold for the surprise-mini gift lowered from >=2 to >=1
 * (and its size changed from 15ml to 10ml at the product level) at the
 * user's request, to promote "10ml de regalo en todas tus compras" for the
 * Fiestas Patrias campaign — same trigger condition as the dispenser now.
 *
 * "Real unit" = any line item whose variant id is neither of the two gift
 * variants above — the gifts never count toward their own or each other's
 * threshold.
 *
 * Runs site-wide (included from layout/theme.liquid, not just the product
 * page) so it reconciles after the cart drawer/cart page mutate the cart on
 * any page, plus once on initial load of every page.
 *
 * Mechanism: reads the live cart via GET /cart.js, decides what (if
 * anything) needs to change, and — only when a change is actually needed —
 * writes it with POST /cart/update.js using an `updates` map keyed by
 * variant id. The UI is then refreshed the same way the rest of this theme
 * does: via the cart-drawer's own getSectionsToRender()/renderContents()
 * (assets/cart-drawer.js) when the drawer is present, and by publishing the
 * theme's own PUB_SUB_EVENTS.cartUpdate event so any other cart-aware
 * component (e.g. the CartItems element on /cart, assets/cart.js) refreshes
 * itself the way it already does for every other cart mutation.
 *
 * Re-entrancy: writing the cart re-fires cartUpdate, which would otherwise
 * call back into this same function. A boolean lock coalesces overlapping
 * calls into a single follow-up run, and once the gifts match the desired
 * state the diff is empty and reconcile() becomes a no-op read.
 */
(function () {
  var GIFT_10ML_ID = 55100408299799;
  var GIFT_DISPENSER_ID = 55100408365335;
  var GIFT_VARIANT_IDS = [GIFT_10ML_ID, GIFT_DISPENSER_ID];
  var PUBLISH_SOURCE = 'nb-auto-gifts';

  var reconciling = false;
  var rerunQueued = false;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function fetchCart() {
    return fetch((window.routes && window.routes.cart_url) ? window.routes.cart_url + '.js' : '/cart.js', {
      headers: { Accept: 'application/json' },
    }).then(function (response) {
      return response.json();
    });
  }

  // Given the current cart, returns only the variant -> quantity changes
  // that are actually needed (empty object when the gifts already match the
  // desired state).
  function computeUpdates(cart) {
    var realQty = 0;
    var currentGiftQty = {};
    currentGiftQty[GIFT_10ML_ID] = 0;
    currentGiftQty[GIFT_DISPENSER_ID] = 0;

    (cart.items || []).forEach(function (item) {
      if (GIFT_VARIANT_IDS.indexOf(item.variant_id) !== -1) {
        currentGiftQty[item.variant_id] += item.quantity;
      } else {
        realQty += item.quantity;
      }
    });

    var want10ml = realQty >= 1;
    var wantDispenser = realQty >= 1;
    var updates = {};

    if (want10ml && currentGiftQty[GIFT_10ML_ID] !== 1) {
      updates[GIFT_10ML_ID] = 1;
    } else if (!want10ml && currentGiftQty[GIFT_10ML_ID] > 0) {
      updates[GIFT_10ML_ID] = 0;
    }

    if (wantDispenser && currentGiftQty[GIFT_DISPENSER_ID] !== 1) {
      updates[GIFT_DISPENSER_ID] = 1;
    } else if (!wantDispenser && currentGiftQty[GIFT_DISPENSER_ID] > 0) {
      updates[GIFT_DISPENSER_ID] = 0;
    }

    return updates;
  }

  function refreshCartUi(cartResponse) {
    var cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function' && cartResponse.sections) {
      cartDrawerEl.renderContents(cartResponse);
    }

    // Let every other cart-aware component (e.g. the full /cart page's
    // <cart-items>) refresh itself exactly the way it already does for any
    // other cart mutation on this theme.
    if (typeof subscribe === 'function' && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: PUBLISH_SOURCE, cartData: cartResponse });
    }
  }

  function applyUpdates(updates) {
    var cartDrawerEl = document.querySelector('cart-drawer');
    var sectionsToRender =
      cartDrawerEl && typeof cartDrawerEl.getSectionsToRender === 'function' ? cartDrawerEl.getSectionsToRender() : null;

    var body = { updates: updates };
    if (sectionsToRender) {
      body.sections = sectionsToRender.map(function (section) {
        return section.section || section.id;
      });
      body.sections_url = window.location.pathname;
    }

    return fetch((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            throw new Error((data && (data.description || data.message)) || 'No se pudo actualizar el carrito');
          }
          return data;
        });
      })
      .then(function (data) {
        refreshCartUi(data);
        return data;
      });
  }

  function reconcile() {
    if (reconciling) {
      rerunQueued = true;
      return Promise.resolve();
    }
    reconciling = true;

    return fetchCart()
      .then(function (cart) {
        var updates = computeUpdates(cart);
        if (Object.keys(updates).length === 0) return null;
        return applyUpdates(updates);
      })
      .catch(function (error) {
        console.warn('[nb-auto-gifts] no se pudieron reconciliar los regalos automáticos:', error);
      })
      .finally(function () {
        reconciling = false;
        if (rerunQueued) {
          rerunQueued = false;
          reconcile();
        }
      });
  }

  function handleCartUpdate(event) {
    // Skip the cartUpdate event we just published ourselves after writing
    // the cart — reconcile() is already idempotent, but there is no reason
    // to fire an extra /cart.js read for it.
    if (event && event.source === PUBLISH_SOURCE) return;
    reconcile();
  }

  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
    } else {
      console.warn('[nb-auto-gifts] pub/sub del tema no disponible; solo se reconciliará al cargar la página.');
    }

    // Also reconcile once per page load, in case the cart already qualifies
    // (or no longer qualifies) for a gift from a previous session.
    reconcile();
  }

  ready(init);
})();


(function () {
  var threshold = 4000000;
  var timer;
  function formatMoney(cents) {
    return '$' + Math.max(0, Math.round(cents / 100)).toLocaleString('es-CL');
  }
  function paint(cart) {
    var total = Number(cart && cart.total_price) || 0;
    var percent = Math.min(100, Math.max(0, Math.round((total / threshold) * 100)));
    document.querySelectorAll('.nb-cart-extras').forEach(function (root) {
      var row = root.querySelector('.nb-shipping-row');
      var text = row && row.querySelector('.nb-cart-extras__text');
      var fill = row && row.querySelector('.nb-shipping-bar__fill');
      var bar = row && row.querySelector('.nb-shipping-bar');
      if (!row || !text || !fill || !bar) return;
      text.innerHTML = total >= threshold ? '🎉 ¡Tu pedido tiene envío gratis!' : '🚚 Te faltan <strong>' + formatMoney(threshold - total) + '</strong> para envío gratis';
      fill.style.width = percent + '%';
      bar.setAttribute('aria-valuenow', String(percent));
      row.classList.toggle('nb-shipping-row--complete', total >= threshold);
    });
  }
  function refresh() {
    window.fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (response) { return response.json(); }).then(paint).catch(function () {});
  }
  function schedule() {
    window.clearTimeout(timer);
    timer = window.setTimeout(refresh, 250);
  }
  refresh();
  ['cart:updated', 'cart:refresh', 'cart:change', 'shopify:section:load'].forEach(function (eventName) {
    document.addEventListener(eventName, schedule);
  });
  document.addEventListener('click', function (event) {
    if (event.target.closest('cart-remove-button, quantity-input, .quantity__button, [name="updates[]"], button[name="checkout"]')) schedule();
  });
  window.setInterval(refresh, 4000);
})();


(function () {
  function formatMoney(cents) {
    return '$' + Math.max(0, Math.round(cents / 100)).toLocaleString('es-CL');
  }
  function paintTotal(cart) {
    var total = Number(cart && cart.total_price) || 0;
    document.querySelectorAll('.nb-cart-checkout-total').forEach(function (node) {
      node.textContent = '· ' + formatMoney(total);
    });
    document.querySelectorAll('#main-cart-footer .totals__total-value').forEach(function (node) {
      node.textContent = formatMoney(total) + ' CLP';
    });
  }
  function refreshTotal() {
    window.fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (response) { return response.json(); }).then(paintTotal).catch(function () {});
  }
  refreshTotal();
  ['cart:updated', 'cart:refresh', 'cart:change', 'shopify:section:load'].forEach(function (eventName) {
    document.addEventListener(eventName, refreshTotal);
  });
  window.setInterval(refreshTotal, 4500);
})();
